import React from 'react';
import { getEditorPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib/index.ts';

import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import {
	VIEW_PAGE,
	VIEW_BLOG_DATE_LEGACY,
	VIEW_BLOG,
} from '@confluence/named-routes/entry-points/routes.ts';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { CONTAINER_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs.ts';

import { ViewPageRouteComponentLoader } from './ViewPageRouteComponentLoader.tsx';
import { ViewPageLiteRoute } from './ViewPageLiteRoute.tsx';
import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';
import type { IEditorPreloader } from '@confluence/edit-button/entry-points/types.ts';

function isLite(query: any) {
	return Boolean(query?.lite);
}

function parseContentSlugFromSSR() {
	return (window?.__SSR_RENDERED__ && window?.__DELAYED_CONTENT_REDIRECTION__?.contentSlug) || '';
}

export const ViewPageOrBlogRoute: {
	(props: RouteMatch): React.JSX.Element;
	NAVIGATION_PARAMS({ name, params, query }: RouteMatch):
		| false
		| {
				Screen: {
					screenEvent: {
						name: string;
						id: string;
						attributes: {
							isPublicView: boolean;
							pageLoadInfo: {
								isLivePage?: boolean | undefined;
								ufoTraceId?: string | undefined;
								ufoTraceType?: string | undefined;
								uuid: string | null;
								contentId: string | null;
								isInitial: boolean;
								isSSR: boolean;
								routingDuration: number;
							};
						};
						objectType: string;
						objectId: string;
					};
					pageState: {
						routeName: string;
						spaceKey: string;
						contentId: string;
						contentType: string;
						contentSlug: string;
					};
					showPremiumOnboarding: boolean;
				};
				MainLayout: {
					navView: string;
					spaceKey: string;
					editorPreloader: () => Promise<() => (config: IEditorPreloader) => JSX.Element>;
					isViewPage: boolean;
				};
		  };
} = (props: RouteMatch): React.JSX.Element => {
	const isMobile = shouldShowMobileWeb();
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const isMobileApp = isMobileApp_DO_NOT_USE();

	const { name, params, query } = props;
	const { contentId, spaceKey } = params;
	const isViewPage = name === VIEW_PAGE.name;

	if (isLite(query)) {
		return <ViewPageLiteRoute {...props} />;
	}

	return (
		<>
			<ViewPageRouteComponentLoader
				spaceKey={spaceKey}
				contentId={contentId}
				isMobile={isMobile}
				isPage={isViewPage}
				// TODO: When we have a new `<MobileAppViewPageLoader />` component for the mobile app, update this component to
				//   pass `isMobileApp` here as an additional prop, and inside the component, check if `isMobileApp` is true and
				//   if so, use the new `<MobileAppViewPageLoader />` component
				// https://product-fabric.atlassian.net/browse/REACT-8691
				isEmbeddedConfluence={isEmbeddedConfluence || isMobileApp}
			/>
		</>
	);
};

ViewPageOrBlogRoute.NAVIGATION_PARAMS = ({
	name,
	params,
	query,
}: RouteMatch):
	| false
	| {
			Screen: {
				screenEvent: {
					name: string;
					id: string;
					attributes: {
						isPublicView: boolean;
						pageLoadInfo: {
							isLivePage?: boolean | undefined;
							ufoTraceId?: string | undefined;
							ufoTraceType?: string | undefined;
							uuid: string | null;
							contentId: string | null;
							isInitial: boolean;
							isSSR: boolean;
							routingDuration: number;
						};
					};
					objectType: string;
					objectId: string;
				};
				pageState: {
					routeName: string;
					spaceKey: string;
					contentId: string;
					contentType: string;
					contentSlug: string;
				};
				showPremiumOnboarding: boolean;
			};
			MainLayout: {
				navView: string;
				spaceKey: string;
				editorPreloader: () => Promise<() => (config: IEditorPreloader) => JSX.Element>;
				isViewPage: boolean;
			};
	  } => {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const isMobileApp = isMobileApp_DO_NOT_USE();
	const showPremiumOnboarding = !isEmbeddedConfluence && !isMobileApp;

	const { contentId, spaceKey, contentSlug } = params;
	const isViewBlog = name === VIEW_BLOG_DATE_LEGACY.name || name === VIEW_BLOG.name;
	const contentType = isViewBlog ? 'blogpost' : 'page';

	if (isLite(query)) {
		return false;
	}

	// Use contentSlug from SSR delayed-redirection if available
	const correctContentSlug = parseContentSlugFromSSR() || contentSlug || '';

	return {
		Screen: {
			screenEvent: {
				name: 'viewPageScreen',
				id: contentId,
				attributes: {
					isPublicView: false,
					pageLoadInfo: getUniquePageLoadId(),
				},
				objectType: contentType,
				objectId: contentId,
			},
			pageState: {
				routeName: name,
				spaceKey,
				contentId,
				contentType,
				contentSlug: correctContentSlug,
			},
			showPremiumOnboarding,
		},
		MainLayout: {
			navView: CONTAINER_HOME_ID,
			spaceKey,
			editorPreloader: getEditorPreloader,
			isViewPage: true,
		},
	};
};
